import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Spin, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API_TENANTS_URL } from "../../../constants";

const FLUENTBIT_OUTPUT = [
  {
    id: 1,
    title: "Splunk",
    description:
      "Splunk allows you to search, analyze, and visualize machine data in real-time—turning logs, events, and metrics into security insights, operational intelligence, and actionable answers.",
    icon: "https://www.splunk.com/content/dam/splunk2/en_us/images/icon-library/footer/logo-splunk-corp-rgb-k-web.svg",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/splunk",
    color: "#0070CD",
    subtitle: "Connect to your Splunk instance via HTTP Event Collector",
  },
  {
    id: 2,
    title: "HTTP",
    description:
      "The HTTP output plugin allows you to flush your records to an HTTP endpoint. It supports both HTTP and HTTPS and provides options for authentication, payload format, and retry mechanisms.",
    icon: "https://cdn-icons-png.flaticon.com/512/2165/2165004.png",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/http",
    color: "#7635DC",
    subtitle: "Send logs to any HTTP/HTTPS endpoint",
  },
  {
    id: 3,
    title: "Syslog",
    description:
      "The Syslog output plugin allows you to deliver records to a Syslog server through TCP or UDP. It follows the RFC5424 protocol standard for message formatting.",
    icon: "https://cdn-icons-png.flaticon.com/512/8888/8888205.png",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/syslog",
    color: "#00A76F",
    subtitle: "Stream logs to your Syslog server via UDP or TCP",
  },
  {
    id: 4,
    title: "Elasticsearch",
    description:
      "The Elasticsearch output plugin allows you to ingest your records into an Elasticsearch database. It supports template management, HTTP authentication, and TLS/SSL encryption.",
    icon: "https://assets.zabbix.com/img/brands/elasticsearch.svg",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/elasticsearch",
    color: "#F6AD37",
    subtitle: "Index logs directly into Elasticsearch",
  },
];

const LOG_TYPES = [
  "Authentication Logs",
  "Database Agents Logs",
  "Platform Audit Logs",
  "RADIUS Logs",
  "Workflow Logs",
];

export default function LogsOutputConfig() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const outputType = searchParams.get("op") || "Splunk";

  const [selectedOutput, setSelectedOutput] = useState(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [connectionTestStatus, setConnectionTestStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [existingConfigs, setExistingConfigs] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get metadata for current output type
  const outputConfig = FLUENTBIT_OUTPUT.find((output) => output.title === outputType) || {
    color: "#666",
    title: `${outputType} Configuration`,
    subtitle: "Configure connection parameters",
    description: "Provide the connection details to integrate with your log destination.",
  };

  // Fetch existing configurations on component mount
  useEffect(() => {
    setLoading(true);

    axios
      .post(
        `${REACT_APP_API_TENANTS_URL}/getLogOutputConfiguration`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
        },
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data && response.data.code !== 500) {
          setExistingConfigs(response.data);
        } else {
          // If error 500, initialize with empty configs
          setExistingConfigs({
            splunk: { Host: "", Port: 0 },
            http: { Host: "", Port: 0 },
            syslog: { Host: "", Port: 0 },
            elasticsearch: { Host: "", Port: 0 },
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching log configurations:", error);
        message.error("Failed to fetch existing configurations");

        // Initialize with empty configs on error
        setExistingConfigs({
          splunk: { Host: "", Port: 0 },
          http: { Host: "", Port: 0 },
          syslog: { Host: "", Port: 0 },
          elasticsearch: { Host: "", Port: 0 },
        });
      });
  }, []);

  // Function to get correct case mapping for output type
  const getOutputTypeKey = (type) => {
    switch (type) {
      case "Splunk":
        return "splunk";
      case "HTTP":
        return "http";
      case "Syslog":
        return "syslog";
      case "Elasticsearch":
        return "elasticsearch";
      default:
        return type.toLowerCase();
    }
  };

  // Check if configuration exists for a specific output type
  const hasConfig = (outputType) => {
    if (!existingConfigs) return false;

    const config = existingConfigs[getOutputTypeKey(outputType)];
    if (!config) return false;

    // Check if at least host and port are defined
    return config.Host && config.Port;
  };

  const handleTestConnection = () => {
    setConnectionTestStatus("testing");
    setTimeout(() => {
      setConnectionTestStatus("success");
    }, 1500);
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      navigate("/");
    }, 1500);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-200">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-semibold text-slate-900">Logs Configuration</h1>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" tip="Loading configurations..." />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content area */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {FLUENTBIT_OUTPUT.map((output) => (
                <div
                  key={output.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:border-slate-300 transition-all duration-200"
                >
                  <div className="h-1" style={{ backgroundColor: output.color }} />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-slate-50 p-3 rounded-lg mr-4">
                        <img
                          src={output.icon}
                          alt={output.title}
                          className="h-8 w-8 object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{output.title}</h3>
                        <p className="text-sm text-slate-500">{output.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">{output.description}</p>

                    {/* Status indicator if config exists */}
                    {hasConfig(output.title) && (
                      <div className="mb-3 flex items-center text-emerald-600">
                        <CheckCircleOutlined className="mr-1" />
                        <span className="text-xs font-medium">Configured</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <Link
                        to={`/pam/settings/logsConfig/output?op=${output.title}`}
                        className="inline-flex items-center px-4 py-2 rounded-md text-white transition-colors"
                        style={{ backgroundColor: hasConfig(output.title) ? "#047857" : "#2563eb" }}
                      >
                        {hasConfig(output.title) ? "Edit" : "Configure"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 ml-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Advanced Configuration Section */}
          </div>

          {/* Right sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">
                About Logs Configuration
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-5">
                We support multiple output destinations for your logs. Choose the one that best fits
                your infrastructure and monitoring needs.
              </p>

              <div className="border-t border-slate-100 pt-5 mt-5">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Configuration Status</h4>
                <ul className="space-y-3 text-sm text-slate-600">
                  {FLUENTBIT_OUTPUT.map((output) => (
                    <li key={output.id} className="flex items-start">
                      <div
                        className={`h-5 w-5 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${
                          hasConfig(output.title)
                            ? "bg-emerald-100 text-emerald-600"
                            : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {hasConfig(output.title) ? (
                          <CheckCircleOutlined style={{ fontSize: "12px" }} />
                        ) : (
                          <span className="text-xs">-</span>
                        )}
                      </div>
                      <span>
                        {output.title}: {hasConfig(output.title) ? "Configured" : "Not configured"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
